import { baseFetch } from "./base";

export type IntegrationCodeResponse = {
  code: string;
  csu_link: string;
  csu_heat_map_link: string;
};

export type CsuIntegrationTarget = "segmentation" | "heatMap";

export const getIntegrationCode =
  async (): Promise<IntegrationCodeResponse> => {
    const response = await baseFetch.get<IntegrationCodeResponse>(
      "/integration/get_code",
    );
    return response.data;
  };

export const buildCsuIntegrationUrl = (
  csuLink: string,
  code: string,
): string => {
  const url = new URL(csuLink);
  url.searchParams.set("one_time_code", code);
  return url.toString();
};

/**
 * Opens CSU in a new tab. A blank tab is opened synchronously on the user
 * gesture so Safari does not treat the post-await navigation as a blocked popup.
 */
export const openCsuIntegration = async (
  target: CsuIntegrationTarget = "heatMap",
): Promise<void> => {
  const newTab = window.open("about:blank", "_blank");

  try {
    const {
      code,
      csu_link: csuLink,
      csu_heat_map_link: csuHeatMapLink,
    } = await getIntegrationCode();

    const trimmedCode = code?.trim() ?? "";
    const trimmedCsuLink =
      (target === "heatMap" ? csuHeatMapLink : csuLink)?.trim() ?? "";

    if (!trimmedCode || !trimmedCsuLink) {
      newTab?.close();
      throw new Error(
        "The integration endpoint returned an empty code or CSU link",
      );
    }

    const url = buildCsuIntegrationUrl(trimmedCsuLink, trimmedCode);

    if (newTab && !newTab.closed) {
      newTab.opener = null;
      newTab.location.replace(url);
      return;
    }

    const fallback = window.open(url, "_blank", "noopener,noreferrer");
    if (!fallback) {
      throw new Error("Popup blocked while opening CSU integration");
    }
  } catch (error) {
    newTab?.close();
    throw error;
  }
};

export const openCsuHeatMap = async (): Promise<void> => {
  await openCsuIntegration("heatMap");
};

export const openCsuSegmentation = async (): Promise<void> => {
  await openCsuIntegration("segmentation");
};
